"use client"
import { toImageSrc } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import Link from "next/link"
import { twMerge } from "tailwind-merge"
import { openShareWindow } from "../../../../../../constants/social-share"
import { DefaultImage } from "../../../../../../lib/external-components"
import { CommonStylesProps } from "../../lib/commonCSSProps"
import { generateResponsiveCSS, isExplicitWidth } from "../../lib/helper"

export interface ImageProps extends CommonStylesProps {
  padding?: string
  border?: string
  heightStyle?: string
  witdhMobile?: string
  widthDesktop?: string
  widthTablet?: string
  heightMobile?: string
  heightTablet?: string
  imageUrl?: string
  borderRadius?: string
  link?: string
  margin?: string
  multiple?: boolean
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down"
  objectPosition?:
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
}

export const RenderConfig: ComponentConfig<ImageProps> = {
  // @TODO: missing responsive CSS\

  render: ({
    padding,
    border,
    witdhMobile,
    widthDesktop,
    widthTablet,
    heightStyle,
    heightMobile,
    heightTablet,
    imageUrl,
    borderRadius,
    margin,
    multiple,
    objectFit,
    objectPosition,
    responsiveType,
    className,
    link,
  }) => {
    const id = `Image-${Math.random().toString(36).substr(2, 9)}`

    // Tailwind preflight sets `img { max-width: 100% }`, which caps the typed width to the
    // parent's width — so an image inside a flex Container collapses instead of staying square.
    // Opt out of the cap (and of flex shrinking) only when an absolute width was typed.
    const sizeAt = (height?: string, width?: string) => ({
      height,
      width,
      display: "block",
      ...(isExplicitWidth(width) ? { maxWidth: "none", flexShrink: "0" } : {}),
    })

    const styles = {
      mobile: sizeAt(heightMobile, witdhMobile),
      tablet: sizeAt(heightTablet, widthTablet),
      desktop: sizeAt(heightStyle, widthDesktop),
    }

    const isFacebookShare = link?.includes("facebook.com/sharer")

    const handleClick = (e: React.MouseEvent) => {
      if (isFacebookShare && link) {
        e.preventDefault()
        openShareWindow(link)
      }
    }

    const imageElement = (
      <Image
        id={id}
        src={imageUrl ? toImageSrc(imageUrl) : DefaultImage}
        alt="placeholder"
        className={`${id} ${twMerge(className)} ${
          objectFit == "cover"
            ? "object-cover"
            : objectFit === "contain"
            ? "object-contain"
            : objectFit === "fill"
            ? "object-fill"
            : objectFit === "scale-down"
            ? "object-scale-down"
            : ""
        } ${
          objectPosition === "top"
            ? "object-top"
            : objectPosition === "bottom"
            ? "object-bottom"
            : objectPosition === "left"
            ? "object-left"
            : objectPosition === "right"
            ? "object-right"
            : objectPosition === "top-left"
            ? "object-left-top"
            : objectPosition === "top-right"
            ? "object-right-top"
            : objectPosition === "bottom-left"
            ? "object-left-bottom"
            : objectPosition === "bottom-right"
            ? "object-right-bottom"
            : "object-center"
        } ${multiple ? "mix-blend-multiply" : ""}`}
        style={{ padding, borderRadius, margin }}
        width={600}
        height={600}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        loading="lazy"
        placeholder="blur"
        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
      />
    )

    return (
      <>
        {generateResponsiveCSS(id, styles || {}, responsiveType)}

        {isFacebookShare ? (
          <div onClick={handleClick} className="block cursor-pointer">
            {imageElement}
          </div>
        ) : link && link.trim() !== "" && link !== "#" ? (
          <Link href={link} className="block">
            {imageElement}
          </Link>
        ) : (
          imageElement
        )}
      </>
    )
  },
}
